import React, { Suspense, lazy, useEffect, useState } from "react";
import { Box, Skeleton, Container, Paper } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useUser } from "../../context/LoginDetail";
import { useModal } from "../../context/ModalContext";
import { get_profile } from "../../api/ControllApi";
import ReferralModal from "../../components/common/ReferralModal";
import { Helmet } from "react-helmet-async";

// Eager Above-The-Fold Components
import Herobanner from "./Herobanner";
import CategorySelector from "./CategorySelector";
import BannerSearch from "./SearchField";

// Lazy-Loaded Sections
const HomeCategories = lazy(() => import("./HomeCategories"));
const FeaturedPlots = lazy(() => import("./FeaturedPlots"));
const ResidentialHomeSlider = lazy(() => import("./ResidentialHomeSlider"));
const GeoCashbackBanner = lazy(() => import("./GeoCashbackBanner"));
const SyndicatePlotsHome = lazy(() => import("./SyndicatePlotsHome"));
const RentalHomeSlider = lazy(() => import("./RentalHomeSlider"));
const GiooPlots = lazy(() => import("./GiooPlots"));
const ServiceCategory = lazy(() => import("./ServiceCategory"));
const TopFooter = lazy(() => import("../../components/footer/Topfooter"));

const SectionSkeleton = ({ height = 300 }) => (
  <Box sx={{ px: 2, py: 2 }}>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={height}
      animation="wave"
      sx={{ borderRadius: 3 }}
    />
  </Box>
);

const Home = () => {
  const { user, loading } = useUser();
  const { showReferral, setShowReferral } = useModal();
  const location = useLocation();
  const [category, setCategory] = useState("all");

  const isLoggedIn = !!user;

  useEffect(() => {
    const isHomePage = location.pathname === "/" || location.pathname === "/home";

    if (isLoggedIn && isHomePage) {
      get_profile()
        .then((res) => {
          const data = res?.data || res;
          const refId = data?.jsondata?.reference_id || data?.reference_id;

          if (Number(refId) === 1) {
            setShowReferral(true);
          } else {
            setShowReferral(false);
          }
        })
        .catch(() => {
          setShowReferral(false);
        });
    } else if (!loading) {
      setShowReferral(false);
    }
  }, [isLoggedIn, user, loading, location.pathname, setShowReferral]);

  return (
    <div style={{ overflowX: "hidden", backgroundColor: "#FDFDFD" }}>
      <Helmet>
        <title>Greenheap Agro Farms | Housing & Developers</title>
        <meta
          name="description"
          content="Greenheap Agro Farms — buy agricultural land, gioo nano plots, syndicate plots, flats & villas and hire professional services."
        />
      </Helmet>

      {/* 1. Hero Banner Slider & Action Overlay */}
      <Herobanner />

      {/* 2. Portfolio Tabs & Search Filter Container */}
      <Container maxWidth="xl" sx={{ mt: 3, position: "relative", zIndex: 10, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: "24px",
            bgcolor: "#FFFFFF",
            boxShadow: "0 12px 36px rgba(0,0,0,0.06)",
            border: "1px solid #EAEAEA",
          }}
        >
          <CategorySelector value={category} onChange={setCategory} />
          <BannerSearch type={category} />
        </Paper>
      </Container>

      {/* 3. Categories Grid (Market Land, Flats/Villa, Gio Nano, Syndicate, Rental Yield) */}
      <Suspense fallback={<SectionSkeleton height={220} />}>
        <HomeCategories />
      </Suspense>

      {/* 4. Featured Residential Plots Section */}
      <Box id="featured-lands">
        <Suspense fallback={<SectionSkeleton height={420} />}>
          <FeaturedPlots />
        </Suspense>
      </Box>

      {/* 5. Featured Flats & Villa Properties Section */}
      <Box id="flats-villas">
        <Suspense fallback={<SectionSkeleton height={420} />}>
          <ResidentialHomeSlider />
        </Suspense>
      </Box>

      {/* 6. Geo Cashback Farms Feature Section */}
      <Suspense fallback={<SectionSkeleton height={500} />}>
        <GeoCashbackBanner />
      </Suspense>

      {/* 7. Featured Syndicate Properties Section */}
      <Box id="syndicate-plots">
        <Suspense fallback={<SectionSkeleton height={420} />}>
          <SyndicatePlotsHome />
        </Suspense>
      </Box>

      {/* 8. Featured Gio Rental Yield Plots Section */}
      <Box id="rental-yield-plots">
        <Suspense fallback={<SectionSkeleton height={420} />}>
          <RentalHomeSlider />
        </Suspense>
      </Box>

      {/* 9. Featured GioNano Plots Section */}
      <Box id="gioo-plots">
        <Suspense fallback={<SectionSkeleton height={420} />}>
          <GiooPlots />
        </Suspense>
      </Box>

      {/* 9. Top Professional Service Categories & Material Store Grid Section */}
      <Box id="material-store">
        <Suspense fallback={<SectionSkeleton height={600} />}>
          <ServiceCategory />
        </Suspense>
      </Box>

      {/* 10. Top Footer Line */}
      <Suspense fallback={<SectionSkeleton height={100} />}>
        <TopFooter />
      </Suspense>

      {/* Referral Popup */}
      <ReferralModal open={showReferral} handleClose={() => setShowReferral(false)} />
    </div>
  );
};

export default Home;
